import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  PeopleAlt as PeopleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { promotionService, studentService, classService } from '../services/apiService';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const Promotion = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  const [pairs, setPairs] = useState([]);
  const [loadingPairs, setLoadingPairs] = useState(true);
  const [pairsError, setPairsError] = useState(null);
  const [selectedPairs, setSelectedPairs] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState({});
  const [selectedTargetClasses, setSelectedTargetClasses] = useState({}); // { classId: targetClassId }
  const [allClasses, setAllClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [managePairId, setManagePairId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [alert, setAlert] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const selectedCardBg = useMemo(() => alpha(theme.palette.primary.main, 0.08), [theme]);

  const decoratePairs = useCallback((data) => {
    return data.map((pair) => ({
      ...pair,
      students: [],
      studentsLoaded: false,
      studentsLoading: false,
      loadError: null,
    }));
  }, []);

  const loadClasses = useCallback(async () => {
    setLoadingClasses(true);
    try {
      const response = await classService.getAll();
      const classesData = response?.data || response || [];
      setAllClasses(Array.isArray(classesData) ? classesData : []);
    } catch (err) {
      console.error('Failed to load classes:', err);
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setLoadingPairs(true);
    setPairsError(null);
    try {
      const response = await promotionService.getSummary();
      const data = response?.data || response || [];
      const decorated = decoratePairs(data);
      setPairs(decorated);
      const initialSelection = {};
      const initialTargetClasses = {};
      decorated.forEach((pair) => {
        initialSelection[pair.class_id] = [];
        // Set default target class to the next upper order
        if (pair.target_class && !pair.is_terminal) {
          initialTargetClasses[pair.class_id] = pair.target_class.class_id;
        }
      });
      setSelectedPairs([]);
      setSelectedStudents(initialSelection);
      setSelectedTargetClasses(initialTargetClasses);
    } catch (err) {
      setPairsError(err?.message || 'Failed to load promotion summary');
    } finally {
      setLoadingPairs(false);
    }
  }, [decoratePairs]);

  useEffect(() => {
    loadClasses();
    loadSummary();
  }, [loadClasses, loadSummary]);

  const handleSelectPair = (classId) => {
    setSelectedPairs((prev) =>
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    );
  };

  const handleSelectAll = () => {
    if (!pairs.length) {
      return;
    }
    if (selectedPairs.length === pairs.length) {
      setSelectedPairs([]);
    } else {
      setSelectedPairs(pairs.map((pair) => pair.class_id));
    }
  };

  const updatePairState = (classId, updater) => {
    setPairs((prev) =>
      prev.map((pair) => (pair.class_id === classId ? { ...pair, ...updater(pair) } : pair))
    );
  };

  const loadPairStudents = async (pair) => {
    updatePairState(pair.class_id, () => ({
      studentsLoading: true,
      loadError: null,
    }));
    try {
      const response = await studentService.getAll({ class_id: pair.class_id });
      const roster = (response?.data || response || []).map((student) => ({
        id: student.id,
        name: student.full_name || student.name || 'Unnamed',
        status: 'ready',
        class_label: `${student.class_name || ''}${
          student.class_section ? ` - ${student.class_section}` : ''
        }`.trim(),
        note: student.roll_number ? `Roll ${student.roll_number}` : '',
      }));

      updatePairState(pair.class_id, () => ({
        students: roster,
        studentsLoaded: true,
        studentsLoading: false,
      }));

      setSelectedStudents((prev) => ({
        ...prev,
        [pair.class_id]: roster.map((student) => student.id),
      }));
      if (!selectedPairs.includes(pair.class_id)) {
        setSelectedPairs((prev) => [...prev, pair.class_id]);
      }
    } catch (err) {
      updatePairState(pair.class_id, () => ({
        studentsLoading: false,
        loadError: err?.message || 'Failed to load students',
      }));
    }
  };

  const handleManageStudents = (pair) => {
    setManagePairId(pair.class_id);
    if (!pair.studentsLoaded && !pair.studentsLoading) {
      loadPairStudents(pair);
    }
  };

  const activePair = useMemo(
    () => pairs.find((pair) => pair.class_id === managePairId) || null,
    [pairs, managePairId]
  );

  const handleStudentToggle = (pairId, studentId, enabled) => {
    if (!enabled) return;
    setSelectedStudents((prev) => {
      const current = prev[pairId] || [];
      const exists = current.includes(studentId);
      const next = exists ? current.filter((id) => id !== studentId) : [...current, studentId];
      return { ...prev, [pairId]: next };
    });
    setSelectedPairs((prev) => (prev.includes(pairId) ? prev : [...prev, pairId]));
  };

  const buildMovesPayload = () => {
    const moves = [];
    pairs.forEach((pair) => {
      if (!selectedPairs.includes(pair.class_id)) return;
      const studentIds = selectedStudents[pair.class_id] || [];
      if (!studentIds.length) return;
      
      // Use selected target class if available, otherwise use default
      const selectedTargetClassId = selectedTargetClasses[pair.class_id];
      let targetClass = null;
      let targetLabel = 'Graduation';
      
      if (pair.is_terminal) {
        targetLabel = 'Alumni';
      } else if (selectedTargetClassId) {
        targetClass = allClasses.find(c => c.id === selectedTargetClassId);
        if (targetClass) {
          targetLabel = `${targetClass.name || ''}${targetClass.section ? ` - ${targetClass.section}` : ''}`.trim();
        } else if (pair.target_class) {
          targetClass = pair.target_class;
          targetLabel = `${pair.target_class.class_name}${
            pair.target_class.section ? ` - ${pair.target_class.section}` : ''
          }`;
        }
      } else if (pair.target_class) {
        targetClass = pair.target_class;
        targetLabel = `${pair.target_class.class_name}${
          pair.target_class.section ? ` - ${pair.target_class.section}` : ''
        }`;
      }
      
      moves.push({
        from_class_id: pair.class_id,
        to_class_id: targetClass ? targetClass.class_id : null,
        student_ids: studentIds,
        label: `${pair.class_name}${pair.section ? ` - ${pair.section}` : ''}`,
        graduate: pair.is_terminal,
        target_label: targetLabel,
      });
    });
    return moves;
  };

  const openConfirmDialog = () => {
    const moves = buildMovesPayload();
    if (!moves.length) {
      setAlert({ type: 'warning', message: 'Select at least one student to promote.' });
      return;
    }
    setConfirmPayload(moves);
    setConfirmOpen(true);
  };

  const handleExecutePromotion = async () => {
    if (!confirmPayload || !confirmPayload.length) return;
    setPromoting(true);
    setAlert(null);
    try {
      const payload = {
        moves: confirmPayload.map(({ from_class_id, to_class_id, student_ids }) => ({
          from_class_id,
          to_class_id,
          student_ids,
        })),
      };
      const response = await promotionService.execute(payload);
      setAlert({
        type: 'success',
        message: response?.message || 'Promotion completed successfully.',
      });
      setConfirmOpen(false);
      setConfirmPayload(null);
      await loadSummary();
    } catch (err) {
      setAlert({
        type: 'error',
        message: err?.message || 'Promotion failed. Please try again.',
      });
    } finally {
      setPromoting(false);
    }
  };

  const selectedCount = selectedPairs.reduce(
    (acc, classId) => acc + ((selectedStudents[classId] || []).length),
    0
  );

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUpIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Promotion Planner
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="text"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadSummary}
                disabled={loadingPairs}
              >
                Refresh
              </Button>
            </Box>

            {alert && (
              <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                {alert.message}
              </Alert>
            )}

            {loadingPairs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : pairsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pairsError}
              </Alert>
            ) : !pairs.length ? (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No classes available for promotion. Please configure class order numbers first.
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 1.5, sm: 2 },
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                }}
              >
                {pairs.map((pair) => {
                  const isSelected = selectedPairs.includes(pair.class_id);
                  const selectedForPair = selectedStudents[pair.class_id]?.length || 0;
                  const isTerminal = pair.is_terminal;
                  return (
                    <Paper
                      key={pair.class_id}
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? selectedCardBg : 'background.paper',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'primary.main',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Stack spacing={1.5} sx={{ flex: 1 }}>
                        {/* Header with class info */}
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                              {pair.class_name}
                              {pair.section ? ` - ${pair.section}` : ''}
                            </Typography>
                            {isSelected && (
                              <CheckCircleOutlineIcon fontSize="small" color="primary" />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Order #{pair.order_number}
                          </Typography>
                        </Box>

                        {/* Arrow and target class dropdown */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                            px: 1.5,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          }}
                        >
                          <TrendingUpIcon fontSize="small" color="primary" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.2 }}>
                              {isTerminal ? 'Graduates To' : 'Promotes To'}
                            </Typography>
                            {isTerminal ? (
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                Alumni
                              </Typography>
                            ) : (
                              <Autocomplete
                                options={allClasses
                                  .filter((cls) => cls.id !== pair.class_id) // Exclude current class
                                  .sort((a, b) => {
                                    const orderA = a.order_number ?? 999;
                                    const orderB = b.order_number ?? 999;
                                    return orderA - orderB;
                                  })}
                                getOptionLabel={(option) => {
                                  if (!option) return '';
                                  const name = option.name || 'Unnamed';
                                  const section = option.section ? ` - ${option.section}` : '';
                                  const order = option.order_number !== null && option.order_number !== undefined ? ` (Order #${option.order_number})` : '';
                                  return `${name}${section}${order}`;
                                }}
                                value={allClasses.find((cls) => cls.id === (selectedTargetClasses[pair.class_id] || pair.target_class?.class_id)) || null}
                                onChange={(event, newValue) => {
                                  setSelectedTargetClasses((prev) => ({
                                    ...prev,
                                    [pair.class_id]: newValue ? newValue.id : '',
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Search and select class..."
                                    size="small"
                                    sx={{
                                      fontSize: '0.875rem',
                                      '& .MuiInputBase-input': {
                                        py: 0.5,
                                      },
                                    }}
                                  />
                                )}
                                ListboxProps={{
                                  style: {
                                    maxHeight: 200,
                                  },
                                }}
                                noOptionsText="No classes found"
                              />
                            )}
                            {!isTerminal && selectedTargetClasses[pair.class_id] && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {(() => {
                                  const selectedClass = allClasses.find(c => c.id === selectedTargetClasses[pair.class_id]);
                                  return selectedClass?.order_number !== null && selectedClass?.order_number !== undefined
                                    ? `Order #${selectedClass.order_number}`
                                    : '';
                                })()}
                              </Typography>
                            )}
                            {!isTerminal && !selectedTargetClasses[pair.class_id] && pair.target_class && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Order #{pair.target_class?.order_number ?? '—'}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Chips row */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          <Chip 
                            size="small" 
                            color="success" 
                            label={`${pair.student_count} active`}
                            sx={{ fontSize: '0.7rem', height: '24px' }}
                          />
                          {isTerminal && (
                            <Chip 
                              size="small" 
                              color="secondary" 
                              label="Graduation"
                              sx={{ fontSize: '0.7rem', height: '24px' }}
                            />
                          )}
                          {isSelected && selectedForPair > 0 && (
                            <Chip
                              size="small"
                              color="info"
                              label={`${selectedForPair} selected`}
                              icon={<PeopleIcon fontSize="small" />}
                              sx={{ fontSize: '0.7rem', height: '24px' }}
                            />
                          )}
                        </Stack>

                        {/* Action buttons */}
                        <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1 }}>
                          <Button
                            variant={isSelected ? 'contained' : 'outlined'}
                            size="small"
                            fullWidth
                            startIcon={isSelected ? <CheckCircleOutlineIcon fontSize="small" /> : null}
                            onClick={() => handleSelectPair(pair.class_id)}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => handleManageStudents(pair)}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Manage
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}

            <Paper
              sx={{
                mt: 3,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  Select students from each class to move them into the next academic order. Terminal classes will
                  graduate selected students and mark them inactive automatically.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={handleSelectAll} disabled={!pairs.length}>
                    {pairs.length && selectedPairs.length === pairs.length ? 'Clear All' : 'Select All'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={openConfirmDialog}
                    disabled={!selectedCount || promoting}
                    startIcon={promoting ? <CircularProgress size={20} color="inherit" /> : <TrendingUpIcon />}
                  >
                    {promoting ? 'Promoting...' : `Promote ${selectedCount || ''}`}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog open={Boolean(activePair)} onClose={() => setManagePairId(null)} fullWidth maxWidth="sm">
        {activePair && (
          <>
            <DialogTitle>
              Manage Students
              <Typography variant="subtitle2" color="text.secondary">
                {activePair.class_name}
                {activePair.section ? ` - ${activePair.section}` : ''} (Order #{activePair.order_number})
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {activePair.studentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : activePair.loadError ? (
                <Alert
                  severity="error"
                  action={
                    <Button size="small" onClick={() => loadPairStudents(activePair)}>
                      Retry
                    </Button>
                  }
                >
                  {activePair.loadError}
                </Alert>
              ) : !activePair.students.length ? (
                <Typography variant="body2" color="text.secondary">
                  No active students found in this class.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {activePair.students.map((student) => {
                    const checked = (selectedStudents[activePair.class_id] || []).includes(student.id);
                    const disabled = student.status !== 'ready';
                    return (
                      <Stack
                        key={student.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.default',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => handleStudentToggle(activePair.class_id, student.id, !disabled)}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {student.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.class_label} {student.note ? `• ${student.note}` : ''}
                          </Typography>
                        </Box>
                        <Chip size="small" color="success" label={student.status} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setManagePairId(null)}>Done</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Promotion</DialogTitle>
        <DialogContent dividers>
          {confirmPayload && confirmPayload.length ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                You are about to move {confirmPayload.reduce((acc, move) => acc + move.student_ids.length, 0)} students
                across {confirmPayload.length} class pair(s). This action cannot be undone.
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                }}
              >
                {confirmPayload.map((move) => (
                  <Box
                    key={move.from_class_id}
                    component="li"
                    sx={{ mb: 1.5, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {move.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      → {move.target_label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {move.student_ids.length} student(s)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2">No students selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={promoting}>
            Cancel
          </Button>
          <Button onClick={handleExecutePromotion} variant="contained" disabled={promoting}>
            {promoting ? 'Promoting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Promotion;

